import React, { useState, useEffect, useCallback, useMemo } from 'react';
import type { ThemeConfig, IntroAnimationType } from '../types/theme';

interface Props {
  animationType: IntroAnimationType;
  title: string;
  subtitle?: string;
  theme: ThemeConfig;
  onComplete: () => void;
}

function isLightColor(hex: string): boolean {
  const color = hex.replace('#', '');
  if (color.length < 6) return true;
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
}

export default function InvitationAnimation({
  animationType,
  title,
  subtitle,
  theme,
  onComplete,
}: Props) {
  const [phase, setPhase] = useState<'waiting' | 'revealing' | 'exiting'>('waiting');

  const fontFamily = theme.fontFamily || 'IntroRust';
  const textColor = theme.textColor || '#ffffff';
  const accentColor = theme.buttonColor || '#c9a96e';
  const bgImage = theme.backgroundImage;
  const isTextLight = isLightColor(textColor);
  const glassBackground = isTextLight
    ? 'rgba(0, 0, 0, 0.6)'
    : 'rgba(255, 255, 255, 0.85)';
  const glassBorder = isTextLight
    ? 'rgba(255, 255, 255, 0.15)'
    : 'rgba(0, 0, 0, 0.1)';

  const handleOpen = useCallback(() => {
    if (phase === 'waiting') {
      setPhase('revealing');
    } else if (phase === 'revealing') {
      setPhase('exiting');
      setTimeout(onComplete, 600);
    }
  }, [phase, onComplete]);

  useEffect(() => {
    const revealTimer = setTimeout(() => {
      if (phase === 'waiting') setPhase('revealing');
    }, 1200);
    return () => clearTimeout(revealTimer);
  }, [phase]);

  useEffect(() => {
    if (phase === 'revealing') {
      const exitTimer = setTimeout(() => {
        setPhase('exiting');
        setTimeout(onComplete, 600);
      }, 5000);
      return () => clearTimeout(exitTimer);
    }
  }, [phase, onComplete]);

  const particles = useMemo(() => {
    if (animationType === 'sparkle') {
      return Array.from({ length: 40 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: 2 + Math.random() * 4,
        delay: Math.random() * 2.5,
        duration: 2 + Math.random() * 2,
        opacity: 0.3 + Math.random() * 0.7,
      }));
    }
    if (animationType === 'petals') {
      return Array.from({ length: 25 }, (_, i) => ({
        id: i,
        x: Math.random() * 120 - 10,
        size: 8 + Math.random() * 16,
        delay: Math.random() * 4,
        duration: 5 + Math.random() * 4,
        rotation: Math.random() * 360,
        drift: -30 + Math.random() * 60,
      }));
    }
    return [];
  }, [animationType]);

  const baseOverlay: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'opacity 0.6s ease-out',
    opacity: phase === 'exiting' ? 0 : 1,
    pointerEvents: phase === 'exiting' ? 'none' : 'auto',
  };

  const bgLayer: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    backgroundImage: bgImage ? `url(${bgImage})` : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    filter: phase === 'revealing' ? 'blur(0px)' : 'blur(4px)',
    transform: phase === 'revealing' ? 'scale(1)' : 'scale(1.05)',
    transition: 'filter 1.5s ease-out, transform 1.5s ease-out',
  };

  const dimLayer: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    background: phase === 'revealing'
      ? 'radial-gradient(ellipse at center, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.7) 100%)'
      : 'rgba(0,0,0,0.75)',
    transition: 'background 1.2s ease-out',
  };

  const glassCardStyle: React.CSSProperties = {
    background: glassBackground,
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: `1px solid ${glassBorder}`,
    borderRadius: '16px',
    padding: '2.5rem 2rem',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  };

  const textContent = (extraClass = '', withGlass = true) => (
    <div
      className={`inv-text-block ${extraClass} ${phase === 'revealing' ? 'inv-text-visible' : ''}`}
      style={{
        fontFamily,
        color: textColor,
        textAlign: 'center',
        position: 'relative',
        zIndex: 10,
        ...(withGlass ? glassCardStyle : {}),
      }}
    >
      {subtitle && (
        <p className="inv-anim-subtitle">{subtitle}</p>
      )}
      <h1 className="inv-anim-title" style={{ textShadow: isTextLight ? '0 2px 8px rgba(0,0,0,0.5), 0 0 2px rgba(0,0,0,0.3)' : '0 1px 4px rgba(255,255,255,0.3)' }}>{title}</h1>
      <div className="inv-anim-ornament" style={{ background: accentColor }} />
      <p className="inv-anim-prompt" style={{ color: accentColor }}>
        {phase === 'waiting' ? 'Opening...' : 'Tap anywhere to continue'}
      </p>
    </div>
  );

  if (animationType === 'envelope') {
    const doorStyle: React.CSSProperties = {
      backgroundImage: bgImage ? `url(${bgImage})` : `linear-gradient(145deg, ${accentColor}dd, ${accentColor}88)`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };

    return (
      <div style={baseOverlay} onClick={handleOpen}>
        <div style={bgLayer} />
        <div style={dimLayer} />
        <div className={`inv-door-wrapper ${phase}`}>
          <div className="inv-door-panel inv-door-left" style={doorStyle}>
            <div className="inv-door-border" style={{ borderColor: `${accentColor}88` }} />
            <div className="inv-door-inner-border" style={{ borderColor: `${accentColor}55` }} />
          </div>
          <div className="inv-door-panel inv-door-right" style={doorStyle}>
            <div className="inv-door-border" style={{ borderColor: `${accentColor}88` }} />
            <div className="inv-door-inner-border" style={{ borderColor: `${accentColor}55` }} />
          </div>
          <div className="inv-door-seal" style={{ background: accentColor, boxShadow: `0 4px 20px ${accentColor}66` }}>
            <span style={{ fontFamily, color: isLightColor(accentColor) ? '#1a1a2e' : '#ffffff' }}>
              {title.charAt(0)}
            </span>
          </div>
          <div className="inv-door-content">
            {textContent('', true)}
          </div>
        </div>
      </div>
    );
  }

  if (animationType === 'curtain') {
    const curtainStyle: React.CSSProperties = bgImage
      ? { backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
      : { background: `linear-gradient(180deg, ${accentColor} 0%, ${accentColor}cc 100%)` };

    return (
      <div style={baseOverlay} onClick={handleOpen}>
        <div style={bgLayer} />
        <div style={dimLayer} />
        <div className={`inv-curtain-wrapper ${phase}`}>
          <div className="inv-curtain-panel inv-curtain-l" style={curtainStyle}>
            <div className="inv-curtain-folds inv-curtain-folds-l" />
          </div>
          <div className="inv-curtain-panel inv-curtain-r" style={curtainStyle}>
            <div className="inv-curtain-folds inv-curtain-folds-r" />
          </div>
          <div className="inv-curtain-valance" style={{ background: bgImage ? undefined : accentColor }} />
          <div className="inv-curtain-rope" style={{ background: accentColor, boxShadow: `0 4px 12px ${accentColor}66` }}>
            <div className="inv-curtain-rope-knot" style={{ background: accentColor, border: `2px solid ${accentColor}` }} />
          </div>
        </div>
        {textContent('inv-curtain-text', true)}
      </div>
    );
  }

  if (animationType === 'fade') {
    return (
      <div style={baseOverlay} onClick={handleOpen}>
        <div style={bgLayer} />
        <div style={dimLayer} />
        <div className="inv-fade-glow" style={{ background: `radial-gradient(circle, ${accentColor}30 0%, transparent 70%)` }} />
        {textContent('inv-fade-text', true)}
      </div>
    );
  }

  if (animationType === 'zoom') {
    return (
      <div style={baseOverlay} onClick={handleOpen}>
        <div style={bgLayer} />
        <div style={dimLayer} />
        <div className={`inv-zoom-ring ${phase}`} style={{ borderColor: `${accentColor}66` }} />
        <div className={`inv-zoom-ring inv-zoom-ring-2 ${phase}`} style={{ borderColor: `${accentColor}33` }} />
        {textContent('inv-zoom-text', true)}
      </div>
    );
  }

  if (animationType === 'flip') {
    return (
      <div style={baseOverlay} onClick={handleOpen}>
        <div style={bgLayer} />
        <div style={dimLayer} />
        <div className={`inv-flip-wrapper ${phase}`}>
          <div className="inv-flip-card-inner">
            <div className="inv-flip-front" style={{ background: `linear-gradient(145deg, ${accentColor}, ${accentColor}cc)`, boxShadow: `0 20px 60px ${accentColor}44` }}>
              <div className="inv-flip-monogram" style={{ color: isLightColor(accentColor) ? '#1a1a2e' : '#ffffff', fontFamily }}>
                {title.charAt(0)}
              </div>
            </div>
            <div className="inv-flip-back" style={{ ...glassCardStyle, position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '16px' }}>
              {textContent('', false)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (animationType === 'sparkle') {
    return (
      <div style={baseOverlay} onClick={handleOpen}>
        <div style={bgLayer} />
        <div style={dimLayer} />
        <div className={`inv-sparkle-field ${phase}`}>
          {particles.map((p) => (
            <span
              key={p.id}
              className="inv-sparkle-particle"
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                width: `${p.size}px`,
                height: `${p.size}px`,
                animationDelay: `${p.delay}s`,
                animationDuration: `${p.duration}s`,
                opacity: p.opacity,
                background: p.id % 3 === 0 ? accentColor : p.id % 3 === 1 ? textColor : `${accentColor}88`,
                boxShadow: `0 0 ${p.size * 2}px ${p.id % 3 === 0 ? accentColor : textColor}`,
              }}
            />
          ))}
        </div>
        {textContent('inv-sparkle-text', true)}
      </div>
    );
  }

  if (animationType === 'petals') {
    return (
      <div style={baseOverlay} onClick={handleOpen}>
        <div style={bgLayer} />
        <div style={dimLayer} />
        <div className={`inv-petals-field ${phase}`}>
          {particles.map((p) => (
            <span
              key={p.id}
              className="inv-petal-item"
              style={{
                left: `${p.x}%`,
                width: `${p.size}px`,
                height: `${p.size * 0.7}px`,
                animationDelay: `${p.delay}s`,
                animationDuration: `${p.duration}s`,
                '--petal-drift': `${p.drift}px`,
                '--petal-rotation': `${p.rotation}deg`,
                background: p.id % 3 === 0
                  ? 'linear-gradient(135deg, #ffc2d1, #ffb3c6)'
                  : p.id % 3 === 1
                  ? 'linear-gradient(135deg, #ffccd5, #feb3c1)'
                  : `linear-gradient(135deg, ${accentColor}88, ${accentColor}44)`,
              } as React.CSSProperties}
            />
          ))}
        </div>
        {textContent('inv-petals-text', true)}
      </div>
    );
  }

  return null;
}
